import React, { useState, useEffect } from 'react';
import { 
  X, Lock, Mail, Eye, EyeOff, AlertCircle, BarChart3, Users, Globe, 
  FileText, LogOut, RefreshCw, TrendingUp, MousePointer, MapPin, Download, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminModal = ({ isOpen, onClose }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem('lenoda_admin_token');
      if (token) {
        setIsLoggedIn(true);
        fetchStats(token);
      }
    }
  }, [isOpen]);

  const fetchStats = async (token) => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('lenoda_admin_token');
        setIsLoggedIn(false);
        return;
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('lenoda_admin_token', data.token);
        setIsLoggedIn(true);
        toast.success('Connexion réussie');
        fetchStats(data.token);
      } else {
        setError(data.detail || 'Identifiants incorrects');
        toast.error('Échec de la connexion');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
      toast.error('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lenoda_admin_token');
    setIsLoggedIn(false);
    setStats(null);
    setEmail('');
    setPassword('');
    toast.success('Déconnexion réussie');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-600 rounded-xl flex items-center justify-center">
              {isLoggedIn ? <BarChart3 className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {isLoggedIn ? 'Dashboard Administrateur' : 'Accès Administrateur'}
              </h2>
              <p className="text-xs text-slate-400">LENODA</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isLoggedIn && (
              <>
                <button
                  onClick={() => fetchStats(localStorage.getItem('lenoda_admin_token'))}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="w-10 h-10 bg-slate-700 hover:bg-slate-600 rounded-xl flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-slate-300" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {!isLoggedIn ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="max-w-md mx-auto">
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      placeholder="admin@lenoda.com"
                      required
                      data-testid="admin-modal-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      placeholder="••••••••••"
                      required
                      data-testid="admin-modal-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                data-testid="admin-modal-login-btn"
                className="w-full mt-6 py-4 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Connexion...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Se connecter
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Dashboard */
            <div>
              {statsLoading && !stats ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-10 h-10 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin"></div>
                </div>
              ) : (
                <>
                  {/* Overview Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-5 h-5 text-sky-400" />
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{stats?.totalVisits || 0}</p>
                      <p className="text-xs text-slate-400">Visites</p>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-5 h-5 text-amber-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{stats?.proofWallClicks || 0}</p>
                      <p className="text-xs text-slate-400">Mur de Preuves</p>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-emerald-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{stats?.totalContacts || 0}</p>
                      <p className="text-xs text-slate-400">Contacts</p>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-purple-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{stats?.dealRoomRequests || 0}</p>
                      <p className="text-xs text-slate-400">Deal Room</p>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-5 h-5 text-pink-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">{stats?.uniqueCountries || 0}</p>
                      <p className="text-xs text-slate-400">Pays</p>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Geographic Distribution */}
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-sky-400" />
                        Provenance Géographique
                      </h3>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {stats?.geoDistribution?.length > 0 ? (
                          stats.geoDistribution.slice(0, 8).map((geo, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{geo.flag || '🌍'}</span>
                                <span className="text-sm text-slate-300">{geo.country || 'Inconnu'}</span>
                              </div>
                              <span className="text-sm text-slate-400">{geo.count}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-500 text-sm text-center py-4">Aucune donnée</p>
                        )}
                      </div>
                    </div>

                    {/* PDF Downloads */}
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                        <Download className="w-4 h-4 text-emerald-400" />
                        Téléchargements PDF
                      </h3>
                      <div className="mb-3 p-3 bg-emerald-600/10 border border-emerald-500/30 rounded-lg">
                        <p className="text-xl font-bold text-emerald-400">{stats?.totalDownloads || 0}</p>
                        <p className="text-xs text-slate-400">Total téléchargements</p>
                      </div>
                      <div className="space-y-2">
                        {stats?.pdfDownloads?.length > 0 ? (
                          stats.pdfDownloads.map((pdf, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-sm text-slate-300 truncate max-w-[150px]">{pdf.document_name}</span>
                              <span className="text-sm text-emerald-400 font-semibold">{pdf.count}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-500 text-sm text-center py-2">Aucun téléchargement</p>
                        )}
                      </div>
                    </div>

                    {/* Document Clicks */}
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:col-span-2">
                      <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                        <MousePointer className="w-4 h-4 text-amber-400" />
                        Documents Consultés
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {stats?.documentClicks?.length > 0 ? (
                          stats.documentClicks.slice(0, 8).map((doc, i) => (
                            <div key={i} className="bg-slate-700/30 rounded-lg p-2 flex items-center justify-between">
                              <span className="text-xs text-slate-300 truncate max-w-[100px]">{doc.document}</span>
                              <span className="text-xs text-amber-400 font-semibold">{doc.clicks}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-500 text-sm text-center py-4 col-span-4">Aucune donnée</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
